/**
 * Everything a session says about itself while nobody is shooting.
 *
 * Eleven messages on one reliable, ordered channel: who is here, who is ready, who they are, who
 * left, who is coming back, how the room is being played, and that it is over. All of it is
 * human-cadence, a handful of bytes a second at worst, so none of it is worth a delta or a
 * quantizer.
 *
 * A guest sends exactly six of the eleven. Everything else is the host's, for the same reason the
 * host steps the world: one machine decides. In particular the roster is full state rather than a
 * change list, because a guest is connected only to the host and has no other way of learning that
 * a third player exists, and full state cannot drift.
 *
 * `seat` goes to one guest rather than to the room. It carries the token that seat is reclaimed
 * with, and a broadcast would hand every guest everybody else's key to everybody else's chair.
 *
 * ```text
 * byte 0  event tag
 * byte 1+ per tag, below
 * ```
 *
 * A tag, like a message kind and a field order, is assigned once and never reused. An older build
 * on the other end would read the new meaning as the old one, which is the kind of bug that shows
 * up as somebody else's game behaving strangely.
 */

import { createReader, createWriter } from '@/net/wire/byteCursor';
import { readSheet, writeSheet } from '@/net/wire/characterSheetCodec';
import { sanitizePlayerName } from '@/identity/playerNameSanitize';
import { packRules, unpackRules } from '@/net/session/sessionRules';
import type { SessionRules } from '@/net/session/sessionRules';
import { NetError } from '@/net/session/sessionErrors';
import type { ByteReader, ByteWriter } from '@/net/wire/byteCursor';
import type { CharacterSheet } from '@/sim/progression/characterSheet';
import type { LinkQuality } from '@/net/lobby/lobbyQuality';

/**
 * Comfortably past a four-seat roster with long names and full sheets, and nowhere near a datagram
 * limit. The worst case is the roster: a tag, a count, then four seats of three fixed bytes, a
 * length byte, twenty-four characters at four UTF-8 bytes each and a maxed sheet, which is
 * `2 + 4 * (3 + 1 + 96 + 59)`, or 638.
 */
const MAX_LOBBY_BYTES = 1024;

/** Eight random bytes: enough that guessing one is not a way into somebody's seat. */
export const LOBBY_TOKEN_BYTES = 8;

/** A token on the wire is raw bytes; everywhere else it is the hex it reads as. */
const TOKEN_CHARS = LOBBY_TOKEN_BYTES * 2;

const TAGS = {
  ready: 1,
  roster: 2,
  left: 3,
  ending: 4,
  seat: 5,
  rejoin: 6,
  leaving: 7,
  arrived: 8,
  rules: 9,
  name: 10,
  sheet: 11,
} as const;

/**
 * Why somebody is no longer in the player list. Each reads differently to the people left.
 *
 * `quit` needs its own message from the guest rather than being read off a closing connection,
 * because a connection closing looks identical whether somebody pressed a button or shut a lid, and
 * only one of those is worth holding a seat for.
 */
export type LeftReason = 'quit' | 'lost' | 'timedOut';

const LEFT_REASONS = { quit: 1, lost: 2, timedOut: 3 } as const;

/** Why the session is over. Only one way today, and the frame is built to carry a second. */
export type EndingReason = 'hostLeft';

const ENDING_REASONS = { hostLeft: 1 } as const;

/**
 * The quality byte, packed: the path in the low two bits, the latency band in the next two.
 *
 * Two readings in the byte one used to hold, rather than a second byte, because a roster is full
 * state broadcast on every change and four bits of it were already spare. The top four are unused
 * and are masked off on the way in, so a build that finds a job for them is not a build this one
 * throws at.
 */
const PATHS = { unknown: 0, direct: 1, relayed: 2, weak: 3 } as const;

const BANDS = { unmeasured: 0, good: 1, fair: 2, poor: 3 } as const;

const QUALITY_FIELD = 0b11;

const BAND_SHIFT = 2;

const READY_BIT = 1;
const HOST_BIT = 2;
const HELD_BIT = 4;

export interface RosterSeat {
  readonly playerId: number;
  readonly name: string;
  /**
   * Who is sitting there, before gear and before the moment.
   *
   * Full state like the rest of the roster, and the host's copy rather than the guest's: the host
   * steps every body in the session, so what it accepted is what the world runs on. A seat whose
   * owner has not announced itself yet reads as the baseline, which is the body it would have had.
   */
  readonly sheet: CharacterSheet;
  readonly ready: boolean;
  readonly host: boolean;
  /** True while the seat is being kept for somebody who dropped. */
  readonly held: boolean;
  readonly quality: LinkQuality;
}

export type LobbyMessage =
  | { readonly kind: 'ready'; readonly ready: boolean }
  | { readonly kind: 'roster'; readonly seats: readonly RosterSeat[] }
  | { readonly kind: 'left'; readonly playerId: number; readonly reason: LeftReason }
  | { readonly kind: 'ending'; readonly reason: EndingReason }
  | { readonly kind: 'seat'; readonly playerId: number; readonly token: string }
  | { readonly kind: 'rejoin'; readonly token: string }
  | { readonly kind: 'leaving' }
  | { readonly kind: 'arrived' }
  /** How the room is being played. Host only, and full state: one byte cannot drift usefully. */
  | { readonly kind: 'rules'; readonly rules: SessionRules }
  /**
   * What a guest would like to be called. Sent on arriving, and again on every re-roll.
   *
   * A guest says its own name rather than the host inventing one, and the host is still the one
   * that publishes it: it lands in the seat table, is sanitized on the way in, and reaches the room
   * in the next roster like every other fact about who is here.
   */
  | { readonly kind: 'name'; readonly name: string }
  /**
   * Who a guest is playing as. Sent on arriving, and again whenever the connection is remade.
   *
   * Announced rather than asked for, the way a name is, and the host is again the one that
   * publishes it. Until it lands, the seat has no character in it and the host gives that player no
   * body: a body created on the baseline and re-sheeted a moment later would be a correction on a
   * guest that had already started predicting from its own perks.
   */
  | { readonly kind: 'sheet'; readonly sheet: CharacterSheet };

export function encodeLobby(message: LobbyMessage): Uint8Array {
  const writer = createWriter(MAX_LOBBY_BYTES);

  switch (message.kind) {
    case 'ready':
      writer.u8(TAGS.ready);
      writer.u8(message.ready ? 1 : 0);
      break;

    case 'roster':
      writer.u8(TAGS.roster);
      writer.u8(message.seats.length);
      for (const seat of message.seats) writeSeat(writer, seat);
      break;

    case 'left':
      writer.u8(TAGS.left);
      writer.u8(message.playerId);
      writer.u8(LEFT_REASONS[message.reason]);
      break;

    case 'ending':
      writer.u8(TAGS.ending);
      writer.u8(ENDING_REASONS[message.reason]);
      break;

    case 'seat':
      writer.u8(TAGS.seat);
      writer.u8(message.playerId);
      writeToken(writer, message.token);
      break;

    case 'rejoin':
      writer.u8(TAGS.rejoin);
      writeToken(writer, message.token);
      break;

    case 'leaving':
      writer.u8(TAGS.leaving);
      break;

    case 'arrived':
      writer.u8(TAGS.arrived);
      break;

    case 'rules':
      writer.u8(TAGS.rules);
      writer.u8(packRules(message.rules));
      break;

    case 'name':
      writer.u8(TAGS.name);
      writeName(writer, message.name);
      break;

    case 'sheet':
      writer.u8(TAGS.sheet);
      writeSheet(writer, message.sheet);
      break;
  }

  return writer.bytes();
}

/** Throws on anything it cannot read in full. A half-read roster is a player list that lies. */
export function decodeLobby(payload: Uint8Array): LobbyMessage {
  const reader = createReader(payload);
  const tag = reader.u8();

  switch (tag) {
    case TAGS.ready:
      return { kind: 'ready', ready: reader.u8() !== 0 };

    case TAGS.roster:
      return { kind: 'roster', seats: readSeats(reader) };

    case TAGS.left:
      return { kind: 'left', playerId: reader.u8(), reason: leftReason(reader.u8()) };

    case TAGS.ending:
      return { kind: 'ending', reason: endingReason(reader.u8()) };

    case TAGS.seat:
      return { kind: 'seat', playerId: reader.u8(), token: readToken(reader) };

    case TAGS.rejoin:
      return { kind: 'rejoin', token: readToken(reader) };

    case TAGS.leaving:
      return { kind: 'leaving' };

    case TAGS.arrived:
      return { kind: 'arrived' };

    case TAGS.rules:
      return { kind: 'rules', rules: unpackRules(reader.u8()) };

    case TAGS.name:
      return { kind: 'name', name: readName(reader) };

    case TAGS.sheet:
      return { kind: 'sheet', sheet: readSheet(reader) };

    default:
      throw new NetError('malformedFrame', `lobby tag ${String(tag)} is not one this build sends`);
  }
}

function writeSeat(writer: ByteWriter, seat: RosterSeat): void {
  writer.u8(seat.playerId);
  writer.u8((seat.ready ? READY_BIT : 0) | (seat.host ? HOST_BIT : 0) | (seat.held ? HELD_BIT : 0));
  writer.u8(PATHS[seat.quality.path] | (BANDS[seat.quality.band] << BAND_SHIFT));
  writeName(writer, seat.name);
  writeSheet(writer, seat.sheet);
}

/** A length byte then UTF-8. The same field in a roster seat and in a guest's own announcement. */
function writeName(writer: ByteWriter, name: string): void {
  const bytes = new TextEncoder().encode(name);
  if (bytes.byteLength > 0xff) {
    throw new NetError('malformedFrame', 'a player name has to fit in a byte of length');
  }

  writer.u8(bytes.byteLength);
  writer.raw(bytes);
}

/**
 * Sanitized here rather than at the far end, because this is where bytes somebody else chose become
 * a string this build renders. Every reader of a name gets the same guarantee, and none of them has
 * to remember to ask for it.
 */
function readName(reader: ByteReader): string {
  return sanitizePlayerName(new TextDecoder().decode(reader.raw(reader.u8())));
}

function readSeats(reader: ByteReader): readonly RosterSeat[] {
  const count = reader.u8();
  const seats: RosterSeat[] = [];

  for (let index = 0; index < count; index += 1) {
    const playerId = reader.u8();
    const flags = reader.u8();
    const quality = linkQuality(reader.u8());
    const name = readName(reader);
    const sheet = readSheet(reader);

    seats.push({
      playerId,
      name,
      sheet,
      ready: (flags & READY_BIT) !== 0,
      host: (flags & HOST_BIT) !== 0,
      held: (flags & HELD_BIT) !== 0,
      quality,
    });
  }

  return seats;
}

/** A token is hex everywhere it is compared and raw bytes on the wire. */
function writeToken(writer: ByteWriter, token: string): void {
  if (token.length !== TOKEN_CHARS || !/^[0-9a-f]+$/.test(token)) {
    throw new NetError('malformedFrame', `a rejoin token is ${String(TOKEN_CHARS)} hex characters`);
  }
  const bytes = new Uint8Array(LOBBY_TOKEN_BYTES);
  for (let index = 0; index < LOBBY_TOKEN_BYTES; index += 1) {
    bytes[index] = Number.parseInt(token.slice(index * 2, index * 2 + 2), 16);
  }
  writer.raw(bytes);
}

function readToken(reader: ByteReader): string {
  return [...reader.raw(LOBBY_TOKEN_BYTES)]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function leftReason(code: number): LeftReason {
  const found = named(LEFT_REASONS, code);
  if (found === null) {
    throw new NetError(
      'malformedFrame',
      `leaving reason ${String(code)} is not one this build has`,
    );
  }
  return found;
}

function endingReason(code: number): EndingReason {
  const found = named(ENDING_REASONS, code);
  if (found === null) {
    throw new NetError('malformedFrame', `ending reason ${String(code)} is not one this build has`);
  }
  return found;
}

/**
 * An unreadable half of the byte reads as unknown or unmeasured, which is exactly what it is and
 * costs nobody anything. Quality is cosmetic, and a future value is not worth dropping somebody's
 * connection over.
 */
function linkQuality(code: number): LinkQuality {
  return {
    path: named(PATHS, code & QUALITY_FIELD) ?? 'unknown',
    band: named(BANDS, (code >> BAND_SHIFT) & QUALITY_FIELD) ?? 'unmeasured',
  };
}

function named<T extends string>(table: Record<T, number>, code: number): T | null {
  const keys = Object.keys(table) as T[];
  return keys.find((key) => table[key] === code) ?? null;
}
